import React, { useEffect, useRef, useLayoutEffect } from "react";

interface ShadowDOMWrapperProps {
  html: string;
  styles?: string;
  onMount?: (shadowRoot: ShadowRoot) => void;
  onUnmount?: () => void;
}

const ShadowDOMWrapper: React.FC<ShadowDOMWrapperProps> = ({
  html,
  styles = "",
  onMount,
  onUnmount,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const mountedRef = useRef(false);

  useLayoutEffect(() => {
    if (!hostRef.current || mountedRef.current) return;

    // Create shadow root only once
    if (!hostRef.current.shadowRoot) {
      shadowRootRef.current = hostRef.current.attachShadow({ mode: "open" });
    } else {
      shadowRootRef.current = hostRef.current.shadowRoot;
    }

    mountedRef.current = true;
  }, []);

  useEffect(() => {
    const shadowRoot = shadowRootRef.current;
    if (!shadowRoot) return;

    // Clear and repopulate content
    shadowRoot.innerHTML = "";

    // Add styles if provided
    if (styles) {
      const styleElement = document.createElement("style");
      styleElement.textContent = styles;
      shadowRoot.appendChild(styleElement);
    }

    // Add HTML content
    const container = document.createElement("div");
    container.innerHTML = html;
    shadowRoot.appendChild(container);

    // Call onMount callback with shadow root for setting up event handlers
    if (onMount) {
      onMount(shadowRoot);
    }

    // Cleanup function
    return () => {
      if (onUnmount) {
        onUnmount();
      }
    };
  }, [html, styles, onMount, onUnmount]);

  return <div ref={hostRef} style={{ width: "100%", height: "100%" }} />;
};

export default ShadowDOMWrapper;
